"use client";

import { useState, useEffect, useCallback } from "react";
import { fetchOddsByDate, type BatchMatchOddsResult } from "@/app/(app)/actions/fetchOddsByDate";
import type { FUSportsBettingOperatorOdds } from "fansunited-data-layer";
import { useLocale } from "@/lib/labels/LabelsProvider";
import { LIVESCORE_REFRESH_INTERVAL_MS } from "@/config/livescore";
import type { LivescoreSport } from "@/views/livescore/utils/livescore";

/** Same Map shape as `useServerBatchMatchOdds` so downstream adapters work unchanged. */
type BatchOddsMap = Map<string, FUSportsBettingOperatorOdds[]>;

export interface UseServerOddsByDateOptions {
    /** YYYY-MM-DD (UTC). */
    date: string;
    /** Sport identifier — pass one of the LivescoreSport values. */
    sport: LivescoreSport;
    /** Market types to fetch (default: ["1x2"]). */
    markets?: string[];
    /** Locale for tenant + translations. */
    locale?: string;
    /** Polling interval in ms (default: LIVESCORE_REFRESH_INTERVAL_MS). */
    pollingIntervalMs?: number;
    /** Filter odds by a specific operator id (applied client-side). */
    operatorId?: string;
    /** When false, the hook stays idle — no fetch, no polling. Default true. */
    enabled?: boolean;
}

function toMap(record: BatchMatchOddsResult): BatchOddsMap {
    const map: BatchOddsMap = new Map();
    for (const [key, value] of Object.entries(record)) map.set(key, value);
    return map;
}

/**
 * Fetches every fixture's odds for a single date via the SDK's date-range
 * endpoint — one server round-trip regardless of how many matches are on the
 * board. Poll cadence matches the livescore refresh interval.
 */
export function useServerOddsByDate({
    date,
    sport,
    markets = ["1x2"],
    locale: localeProp,
    pollingIntervalMs = LIVESCORE_REFRESH_INTERVAL_MS,
    operatorId,
    enabled = true,
}: UseServerOddsByDateOptions) {
    const contextLocale = useLocale();
    const locale = localeProp || contextLocale;
    const [oddsMap, setOddsMap] = useState<BatchOddsMap>(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const load = useCallback(
        async (isRefresh = false) => {
            if (!enabled) {
                setOddsMap(new Map());
                setLoading(false);
                return;
            }

            if (!isRefresh) setLoading(true);
            setError(null);

            try {
                const result = await fetchOddsByDate({
                    date,
                    sport,
                    marketTypes: markets as Parameters<typeof fetchOddsByDate>[0]["marketTypes"],
                    locale: locale as Parameters<typeof fetchOddsByDate>[0]["locale"],
                });

                let map = toMap(result);

                if (operatorId) {
                    const filtered: BatchOddsMap = new Map();
                    map.forEach((operators, id) => {
                        const matched = operators.filter((op) => op.operator.id === operatorId);
                        if (matched.length > 0) filtered.set(id, matched);
                    });
                    map = filtered;
                }

                setOddsMap(map);
            } catch (err) {
                console.error("[useServerOddsByDate] Error:", err);
                setError(err instanceof Error ? err : new Error("Failed to fetch date-range odds"));
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [date, sport, markets.join(","), locale, operatorId, enabled]
    );

    useEffect(() => {
        load(false);
    }, [load]);

    useEffect(() => {
        if (!enabled) return;
        const id = setInterval(() => load(true), pollingIntervalMs);
        return () => clearInterval(id);
    }, [load, pollingIntervalMs, enabled]);

    const refetch = useCallback(() => load(true), [load]);

    return { oddsMap, loading, error, refetch };
}
